
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { categories } from '../../../mocks/articles';

export default function ArticlePost() {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    title: '',
    excerpt: '',
    content: '',
    category: '',
    tags: '',
    author: '',
    email: '',
    featured: false,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  // -------------------------------------------------------------------------
  // Handlers
  // -------------------------------------------------------------------------
  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // -------------------------------------------------
    // Basic validation
    // -------------------------------------------------
    if (!formData.title || !formData.author || !formData.email) {
      setSubmitMessage('必須項目をすべて入力してください。');
      return;
    }

    if (formData.excerpt.length > 500) {
      setSubmitMessage('記事の概要は500文字以内で入力してください。');
      return;
    }

    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      // -------------------------------------------------
      // Build comprehensive image queries for different uses
      // -------------------------------------------------
      const baseImageQuery = formData.category && formData.title ? 
        (() => {
          const categoryKeywords: Record<string, string> = {
            'AI技術': 'artificial intelligence machine learning neural networks deep learning technology innovation digital transformation futuristic AI systems',
            'ホログラム': 'holographic projection 3D hologram display technology transparent floating images futuristic holographic interface advanced projection systems',
            '技術動向': 'technology trends innovation digital transformation cutting-edge tech modern technology advancement future technology landscape',
            'イノベーション': 'innovation breakthrough technology advancement revolutionary tech creative solutions disruptive technology modern innovation',
            '研究開発': 'research development laboratory scientific innovation R&D technology research advanced scientific equipment modern lab environment',
            'AI・ロボット': 'artificial intelligence robotics automation machine learning AI robots intelligent systems advanced robotics technology',
            '放送技術': 'broadcast technology media production television radio streaming technology broadcasting equipment modern media systems',
            'ビジネス': 'business technology corporate innovation digital business transformation modern office environment professional business solutions',
            '3DCG・制作': '3D computer graphics digital art creation 3D modeling animation rendering visual effects creative technology digital production',
            'VR・AR': 'virtual reality augmented reality VR headsets AR glasses immersive technology mixed reality futuristic VR AR systems',
            'スタートアップ': 'startup innovation entrepreneurship business incubation modern startup office creative workspace innovative business solutions',
            '医療・ヘルスケア': 'medical technology healthcare innovation digital health medical devices modern hospital equipment healthcare solutions',
            '教育・学習': 'educational technology e-learning digital education smart classroom modern learning environment educational innovation',
            '通信技術': 'telecommunications network technology 5G 6G communication systems wireless technology advanced networking infrastructure'
          };
          
          const categoryKey = categoryKeywords[formData.category] || 'modern technology innovation digital advancement';
          const titleWords = formData.title
            .replace(/[^\w\s\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FAF]/gi, ' ')
            .split(' ')
            .filter(word => word.length > 1)
            .slice(0, 4)
            .join(' ');
          
          return `${categoryKey} ${titleWords} professional clean modern background high quality digital concept futuristic design minimalist style sophisticated technology elegant presentation`;
        })() 
        : 'modern technology innovation professional clean background digital concept high quality minimalist design sophisticated presentation elegant tech';

      // Generate unique sequence numbers for each image
      const timestamp = Date.now();
      const randomSeed = Math.floor(Math.random() * 10000);

      // Create different image variations
      const mainImageQuery = `${baseImageQuery} wide banner format professional header image clean composition`;
      const cardImageQuery = `${baseImageQuery} square format card thumbnail clean simple design`;
      const sidebarImageQuery = `${baseImageQuery} compact square format sidebar thumbnail minimal design`;

      // -------------------------------------------------
      // Assemble article data with properly generated images
      // -------------------------------------------------
      const articleData = {
        id: timestamp,
        title: formData.title,
        excerpt: formData.excerpt,
        content: formData.content,
        category: formData.category,
        tags: formData.tags.split(',').map((t) => t.trim()).filter(t => t.length > 0),
        author: formData.author,
        email: formData.email,
        featured: formData.featured,
        date: new Date().toLocaleDateString('ja-JP'),
        views: Math.floor(Math.random() * 1000) + 50,
        image: `https://public.readdy.ai/ai/img_res/948a13f1eb402f8ff16472e217608fb6.jpg$%7BencodeURIComponent%28cardImageQuery%29%7D&width=600&height=400&seq=${timestamp}-card-${randomSeed}&orientation=landscape`,
        mainImage: `https://readdy.ai/api/search-image?query=$%7BencodeURIComponent%28mainImageQuery%29%7D&width=800&height=500&seq=${timestamp}-main-${randomSeed}&orientation=landscape`,
        sidebarImage: `https://readdy.ai/api/search-image?query=$%7BencodeURIComponent%28sidebarImageQuery%29%7D&width=200&height=200&seq=${timestamp}-sidebar-${randomSeed}&orientation=squarish`,
      };

      // Save to localStorage
      const existing = JSON.parse(localStorage.getItem('userArticles') || '[]');
      existing.unshift(articleData);
      localStorage.setItem('userArticles', JSON.stringify(existing));

      // Notify other parts of the app that a new article exists
      window.dispatchEvent(new CustomEvent('articleUpdated'));

      setSubmitMessage('記事の投稿が完了しました！画像付きで公開されます。');
      setFormData({
        title: '',
        excerpt: '',
        content: '',
        category: '',
        tags: '',
        author: '',
        email: '',
        featured: false,
      });

      // Small delay before redirecting to the article list
      setTimeout(() => {
        navigate('/media/articles');
      }, 1500);
    } catch (err) {
      console.error(err);
      setSubmitMessage('投稿に失敗しました。もう一度お試しください。');
    } finally {
      setIsSubmitting(false);
    }
  };

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-gray-900 text-white py-4">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-6">
              <button
                onClick={() => navigate('/')}
                className="text-2xl font-bold hover:text-blue-400 transition-colors cursor-pointer"
              >
                ホログラム
              </button>
              <span className="text-blue-400 text-lg">MEDIA</span>
            </div>

            <nav className="hidden md:flex items-center space-x-6">
              <button
                onClick={() => navigate('/media')}
                className="hover:text-blue-400 transition-colors cursor-pointer"
              >
                ホーム
              </button>
              <button
                onClick={() => navigate('/media/articles')}
                className="hover:text-blue-400 transition-colors cursor-pointer"
              >
                記事一覧
              </button>
              <button
                onClick={() => navigate('/media/post')}
                className="text-blue-400 cursor-pointer"
              >
                記事を投稿
              </button>
              <button 
                onClick={() => navigate('/admin/login')}
                className="bg-gray-600 hover:bg-gray-700 px-4 py-2 rounded-full transition-colors cursor-pointer whitespace-nowrap"
              >
                管理者
              </button>
            </nav>
          </div>
        </div>
      </header>

      {/* Breadcrumb */}
      <div className="bg-white py-4">
        <div className="max-w-4xl mx-auto px-4">
          <nav className="flex items-center space-x-2 text-sm text-gray-600">
            <button
              onClick={() => navigate('/media')}
              className="hover:text-blue-600 cursor-pointer"
            >
              ホーム
            </button>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-gray-900">記事投稿</span>
          </nav>
        </div>
      </div>

      {/* Form Section */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              記事を投稿
            </h1>
            <p className="text-xl text-gray-600">
              AI・ホログラム技術に関する記事をお投稿ください
            </p>
          </div>

          {submitMessage && (
            <div
              className={`mb-8 p-4 rounded-lg ${
                submitMessage.includes('完了')
                  ? 'bg-green-100 text-green-700 border border-green-200'
                  : 'bg-red-100 text-red-700 border border-red-200'
              }`}
            >
              {submitMessage}
            </div>
          )}

          <div className="bg-white rounded-xl shadow-lg p-8">
            <form
              id="article-submission-form"
              onSubmit={handleSubmit}
              className="space-y-6"
              data-readdy-form
            >
              {/* Author & Email */}
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label
                    htmlFor="author"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    著者名 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="author"
                    name="author"
                    value={formData.author}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="お名前を入力してください"
                  />
                </div>

                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    メールアドレス <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="メールアドレスを入力してください"
                  />
                </div>
              </div>

              {/* Title */}
              <div>
                <label
                  htmlFor="title"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  記事タイトル <span className="text-red-5 0">*</span>
                </label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="記事のタイトルを入力してください"
                />
              </div>

              {/* Category & Tags */}
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label
                    htmlFor="category"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    カテゴリー
                  </label>
                  <select
                    id="category"
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">選択してください</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.name}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label
                    htmlFor="tags"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    タグ（カンマ区切り）
                  </label>
                  <input
                    type="text"
                    id="tags"
                    name="tags"
                    value={formData.tags}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="例: AI, ホログラム, ディープラーニング"
                  />
                </div>
              </div>

              {/* Featured Checkbox */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="featured"
                  name="featured"
                  checked={formData.featured}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                />
                <label
                  htmlFor="featured"
                  className="ml-2 block text-sm text-gray-700"
                >
                  ピックアップ記事に設定
                </label>
              </div>

              {/* Excerpt */}
              <div>
                <label
                  htmlFor="excerpt"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  記事概要（最大500文字）
                </label>
                <textarea
                  id="excerpt"
                  name="excerpt"
                  rows={3}
                  value={formData.excerpt}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="記事の概要を書いてください"
                />
              </div>

              {/* Content */}
              <div>
                <label
                  htmlFor="content"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  本文
                </label>
                <textarea
                  id="content"
                  name="content"
                  rows={8}
                  value={formData.content}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="記事の本文を入力してください"
                />
              </div>

              {/* Submit */}
              <div className="text-center">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`inline-flex items-center px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 whitespace-nowrap`}
                >
                  {isSubmitting ? '投稿中...' : '記事を投稿する'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
